import { useCallback } from "react"

export type SelectionChangeEventHandler = (selection: string[]) => void

export type HeatmapState = {
    selected: Record<string, boolean>
    toggleSelection: (id: string) => void
    select: (ids: string[], clickedId?: string) => void
    deselect: (ids: string[]) => void
    setSelected: (newSelection: Record<string, boolean>, clickedId?: string) => void
}

export function useHeatmapState(
    selected: HeatmapState["selected"],
    updateSelected: (data: Record<string, boolean>, clickedId?: string) => void
): HeatmapState {
    const toggleSelection: HeatmapState["toggleSelection"] = useCallback(
        (id: string) => {
            const selection = {
                ...selected,
                [id]: !selected[id],
            }
            updateSelected(selection)
        },
        [selected, updateSelected]
    )

    const select: HeatmapState["select"] = useCallback(
        (ids: string[], clickedId?: string) => {
            // noop
            if (ids.length === 0) return

            const selection = {
                ...selected,
            } as HeatmapState["selected"]
            for (const id of ids) {
                selection[id] = true
            }
            updateSelected(selection, clickedId)
        },
        [selected, updateSelected]
    )

    const deselect: HeatmapState["deselect"] = useCallback(
        (ids: string[]) => {
            // noop
            if (ids.length === 0) return

            const selection = {
                ...selected,
            } as HeatmapState["selected"]
            for (const id of ids) {
                selection[id] = false
            }
            updateSelected(selection)
        },
        [selected, updateSelected]
    )

    const setSelected = useCallback(
        (newSelected: { [id: string]: boolean }, clickedId?: string) => {
            updateSelected(newSelected, clickedId)
        },
        [updateSelected]
    )

    return {
        selected,
        toggleSelection,
        select,
        deselect,
        setSelected,
    }
}

export const selectionToList = (selected: HeatmapState["selected"]) =>
    Object.keys(selected).filter((id) => selected[id])
